import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { parseTitleNews, type TitleNewsStory } from "../lib/title-news";
import { pauseTitleMenuMusic, playTitleMenuMusic, restartTitleMenuMusic, stopTitleMenuMusic, titleMenuMusic } from "../lib/title-music";
import { playTitleMenuClickSound, playTitleMenuHoverSound, preloadTitleMenuUiAudio } from "../lib/title-ui-audio";
import type { CampaignKind } from "../types";
import { TitleScreenWebGL } from "./TitleScreenWebGL";
import { mediaUrl } from "../lib/media-url";
import styles from "./TbltopTitleScreen.module.css";

type TbltopTitleScreenProps = {
  onStartCampaign: (kind?: CampaignKind) => void;
  onLoadCampaign: (campaignId: string) => Promise<void>;
  onOpenMapCreator: () => void;
  onReturnHome: () => void;
  storedCampaigns: TitleCampaignSummary[];
  storedCampaignsLoading: boolean;
  storedCampaignsError?: string;
};

export type TitleCampaignSummary = {
  id: string;
  name: string;
  kind: CampaignKind;
  sceneCount: number;
  updatedAt: number;
  active: boolean;
};

const CAMPAIGN_KIND_META: Record<CampaignKind, { icon: string; label: string }> = {
  tabletop: { icon: "⚔", label: "Dungeon Tabletop" },
  poker: { icon: "♠", label: "Card Game" },
  broadside: { icon: "⚓", label: "Battleboats" },
};

function campaignDate(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(timestamp));
}

const FALLBACK_NEWS: TitleNewsStory[] = [{
  date: "TBLTOP Dispatch",
  title: "The table is ready",
  summary: "Maps, cards, chips, effects, and player phones are waiting for the next campaign.",
  details: [],
}];

export function TbltopTitleScreen({ onStartCampaign, onLoadCampaign, onOpenMapCreator, onReturnHome, storedCampaigns, storedCampaignsLoading, storedCampaignsError }: TbltopTitleScreenProps) {
  const [news, setNews] = useState<TitleNewsStory[]>(FALLBACK_NEWS);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [campaignPickerOpen, setCampaignPickerOpen] = useState(false);
  const [campaignLibraryOpen, setCampaignLibraryOpen] = useState(false);
  const [loadingCampaignId, setLoadingCampaignId] = useState<string>();
  const [loadCampaignError, setLoadCampaignError] = useState("");
  const [introVisible, setIntroVisible] = useState(true);
  const [musicState, setMusicState] = useState<"starting" | "playing" | "paused" | "blocked">("starting");
  const [uiAudioReady, setUiAudioReady] = useState(false);
  const creditsButtonRef = useRef<HTMLButtonElement>(null);
  const startButtonRef = useRef<HTMLButtonElement>(null);
  const loadButtonRef = useRef<HTMLButtonElement>(null);
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const titleScreenRef = useRef<HTMLElement>(null);
  const uiSoundCountsRef = useRef({ hover: 0, click: 0 });

  const recordUiSound = useCallback((kind: "hover" | "click", source: string | undefined) => {
    const root = titleScreenRef.current;
    if (!root || !source) return;
    const attribute = kind === "hover" ? "uiHoverSoundCount" : "uiClickSoundCount";
    uiSoundCountsRef.current[kind] += 1;
    root.dataset[attribute] = String(uiSoundCountsRef.current[kind]);
    root.dataset.lastUiSound = source;
  }, []);

  const toggleMusic = useCallback(() => {
    const audio = musicRef.current;
    if (!audio) return;
    if (!audio.paused) { pauseTitleMenuMusic(); setMusicState("paused"); return; }
    void playTitleMenuMusic().then((started) => setMusicState(started ? "playing" : "blocked"));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setIntroVisible(false), 4_000);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    setUiAudioReady(preloadTitleMenuUiAudio());
  }, []);

  useEffect(() => {
    const audio = titleMenuMusic();
    musicRef.current = audio;
    const syncPlaying = () => setMusicState("playing");
    const syncPaused = () => setMusicState(audio.currentTime > 0 ? "paused" : "starting");
    audio.addEventListener("play", syncPlaying); audio.addEventListener("pause", syncPaused);
    let disposed = false;
    const removeRetryListeners = () => {
      window.removeEventListener("pointerdown", retryAfterAutoplayBlock, true);
      window.removeEventListener("keydown", retryAfterAutoplayBlock, true);
    };
    const retryAfterAutoplayBlock = (event: Event) => {
      const target = event.target;
      if (target instanceof Element && target.closest("[data-title-music-control]")) return;
      void playTitleMenuMusic().then((started) => {
        if (disposed) return;
        setMusicState(started ? "playing" : "blocked");
        if (started) removeRetryListeners();
      });
    };
    const startFromBeginning = () => {
      removeRetryListeners();
      window.addEventListener("pointerdown", retryAfterAutoplayBlock, true);
      window.addEventListener("keydown", retryAfterAutoplayBlock, true);
      setMusicState("starting");
      void restartTitleMenuMusic().then((started) => {
        if (disposed) return;
        setMusicState(started ? "playing" : "blocked");
        if (started) removeRetryListeners();
      });
    };
    const restartAfterPageRestore = (event: PageTransitionEvent) => { if (event.persisted) startFromBeginning(); };
    startFromBeginning();
    window.addEventListener("pageshow", restartAfterPageRestore);
    return () => {
      disposed = true;
      removeRetryListeners();
      window.removeEventListener("pageshow", restartAfterPageRestore);
      audio.removeEventListener("play", syncPlaying); audio.removeEventListener("pause", syncPaused);
      stopTitleMenuMusic();
      musicRef.current = null;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void fetch("/TBLTOP_NEWS.md", { signal: controller.signal })
      .then((response) => response.ok ? response.text() : Promise.reject(new Error("News unavailable")))
      .then((markdown) => { const stories = parseTitleNews(markdown); if (stories.length) setNews(stories); })
      .catch((error: unknown) => { if (!(error instanceof DOMException && error.name === "AbortError")) setNews(FALLBACK_NEWS); });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!creditsOpen && !campaignPickerOpen && !campaignLibraryOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (creditsOpen) { setCreditsOpen(false); creditsButtonRef.current?.focus(); }
      if (campaignPickerOpen) { setCampaignPickerOpen(false); startButtonRef.current?.focus(); }
      if (campaignLibraryOpen) { setCampaignLibraryOpen(false); setLoadCampaignError(""); loadButtonRef.current?.focus(); }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [campaignLibraryOpen, campaignPickerOpen, creditsOpen]);

  const openCampaignLibrary = () => {
    setCampaignPickerOpen(false);
    setLoadCampaignError("");
    setCampaignLibraryOpen(true);
  };

  const loadCampaign = (campaignId: string) => {
    setLoadingCampaignId(campaignId);
    setLoadCampaignError("");
    void onLoadCampaign(campaignId).catch((error: unknown) => {
      setLoadingCampaignId(undefined);
      setLoadCampaignError(error instanceof Error ? error.message : "That campaign could not be opened.");
    });
  };

  return <main
    ref={titleScreenRef}
    className={styles.page}
    style={{ "--title-background": `url("${mediaUrl("/assets/title/tbltop-main-menu-background.webp")}")` } as CSSProperties}
    data-testid="tbltop-title-screen"
    data-ui-audio-preload={uiAudioReady ? "ready" : "loading"}
    data-ui-hover-sound-count={uiSoundCountsRef.current.hover}
    data-ui-click-sound-count={uiSoundCountsRef.current.click}
    onPointerOverCapture={(event) => {
      if (event.pointerType !== "mouse") return;
      const button = (event.target as Element).closest<HTMLButtonElement>("button");
      if (!button || button.disabled) return;
      const previousTarget = event.relatedTarget;
      if (previousTarget instanceof Node && button.contains(previousTarget)) return;
      recordUiSound("hover", playTitleMenuHoverSound());
    }}
    onClickCapture={(event) => {
      const button = (event.target as Element).closest<HTMLButtonElement>("button");
      if (!button || button.disabled) return;
      recordUiSound("click", playTitleMenuClickSound());
    }}
  >
    <TitleScreenWebGL />
    <div className={styles.lightShafts} aria-hidden="true" />
    <div className={styles.vignette} aria-hidden="true" />
    <div className={styles.frame} aria-hidden="true"><i /><i /><i /><i /></div>

    <div className={`${styles.logoIntro}${introVisible ? "" : ` ${styles.logoIntroFinished}`}`} data-testid="dnai-intro" data-duration-ms="4000" aria-label="DNAI presents" aria-hidden={introVisible ? undefined : "true"}>
      <div className={styles.introBrand}>
        <img crossOrigin="anonymous" src={mediaUrl("/assets/title-screen/dnai-sloth-mark.png")} alt="DNAi" />
        <p>DNAi - We're almost there</p>
      </div>
    </div>

    <button type="button" className={styles.returnHome} onClick={onReturnHome} aria-label="Return to the TBLTOP homepage">
      <span aria-hidden="true">‹</span> TBL.TOP
    </button>

    <section className={styles.newsPanel} aria-labelledby="title-news-heading">
      <div className={styles.panelCrest} aria-hidden="true"><span>TT</span></div>
      <header>
        <p>From the workshop</p>
        <h2 id="title-news-heading">Recent dispatches</h2>
      </header>
      <div className={styles.newsScroll}>
        {news.slice(0, 4).map((story) => <article key={`${story.date}-${story.title}`}>
          <div>
            <time>{story.date}</time>
            <h3>{story.title}</h3>
            <p>{story.summary}</p>
            {story.details.length > 0 && <ul>{story.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>}
          </div>
        </article>)}
      </div>
    </section>

    <section className={styles.titleBlock} aria-labelledby="tbltop-title">
      <p className={styles.overline}><span /> A shared table awaits <span /></p>
      <div className={styles.titleSeal} aria-hidden="true"><span>TT</span></div>
      <h1 id="tbltop-title">TBLTOP</h1>
      <p className={styles.subtitle}>Tabletop Director</p>
      <div className={styles.titleRule} aria-hidden="true"><i /><span>◆</span><i /></div>
      <p className={styles.motto}>Bring the pieces.<br />Direct the world.</p>
    </section>

    <nav className={styles.menuPanel} aria-label="TBLTOP title menu">
      <p className={styles.menuLabel}>Main menu</p>
      <button ref={startButtonRef} type="button" className={`${styles.menuButton} ${styles.startButton}`} onClick={() => setCampaignPickerOpen(true)}>
        <span className={styles.buttonRune} aria-hidden="true">◆</span>
        <span><strong>Start campaign</strong><small>Enter the director&rsquo;s table</small></span>
        <b aria-hidden="true">›</b>
      </button>
      <button ref={loadButtonRef} type="button" className={styles.menuButton} onClick={openCampaignLibrary}>
        <span className={styles.buttonRune} aria-hidden="true">↺</span>
        <span><strong>Load previous campaign</strong><small>Continue a table saved on this device</small></span>
        <b aria-hidden="true">›</b>
      </button>
      <button ref={creditsButtonRef} type="button" className={styles.menuButton} onClick={() => setCreditsOpen(true)}>
        <span className={styles.buttonRune} aria-hidden="true">✦</span>
        <span><strong>Show credits</strong><small>The people behind the table</small></span>
        <b aria-hidden="true">›</b>
      </button>
      <button type="button" className={`${styles.menuButton} ${styles.dosButton}`} disabled title="This ancient portal is not connected yet.">
        <span className={styles.buttonRune} aria-hidden="true">_</span>
        <span><strong>Exit to DOS</strong><small>Floppy drive not found</small></span>
        <b aria-hidden="true">C:\&gt;</b>
      </button>
      <p className={styles.buildLine}><span>LOCAL COMMAND EDITION</span><span>v1.0</span></p>
    </nav>

    <div className={styles.statusLine} aria-hidden="true">
      <span>PROJECTOR</span><i /><span>TABLE SCREEN</span><i /><span>PLAYER PHONES</span>
    </div>

    <button type="button" data-title-music-control="" className={`${styles.musicControl} ${musicState === "blocked" ? styles.musicBlocked : ""}`} onClick={toggleMusic} aria-label={musicState === "playing" ? "Pause main menu music" : "Play main menu music"}>
      <span aria-hidden="true">{musicState === "playing" ? "♪" : "♫"}</span>
      {musicState === "blocked" ? "Play music" : musicState === "playing" ? "Music on" : "Music off"}
    </button>

    {campaignPickerOpen && <div className={styles.modalBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) { setCampaignPickerOpen(false); startButtonRef.current?.focus(); } }}>
      <section className={styles.campaignPicker} role="dialog" aria-modal="true" aria-labelledby="campaign-picker-title">
        <button type="button" className={styles.pickerClose} aria-label="Close game selection" onClick={() => { setCampaignPickerOpen(false); startButtonRef.current?.focus(); }}>×</button>
        <p className={styles.creditsOverline}>Choose your table</p>
        <h2 id="campaign-picker-title">What are we playing?</h2>
        <p className={styles.pickerLead}>Every table opens with the right director tools, player remotes, and shared-screen experience.</p>
        <div className={styles.gameChoiceGrid}>
          <button type="button" autoFocus onClick={() => onStartCampaign("tabletop")}>
            <span className={styles.choiceIcon} aria-hidden="true"><i>⚔</i></span>
            <span><strong>Dungeon Tabletop</strong><small>Maps, miniatures, heroes, fog, weather, and arena effects.</small></span>
            <b aria-hidden="true">01</b>
          </button>
          <button type="button" onClick={() => onStartCampaign("poker")}>
            <span className={styles.choiceIcon} aria-hidden="true"><i>♠</i></span>
            <span><strong>Card Game</strong><small>Private hands, chips, seats, the pot, and a projected table.</small></span>
            <b aria-hidden="true">02</b>
          </button>
          <button type="button" onClick={() => onStartCampaign("broadside")}>
            <span className={styles.choiceIcon} aria-hidden="true"><i>⚓</i></span>
            <span><strong>Battleboats</strong><small>Two private fleets, phone-fired salvos, explosions, and open water.</small></span>
            <b aria-hidden="true">03</b>
          </button>
          <button type="button" onClick={onOpenMapCreator}>
            <span className={styles.choiceIcon} aria-hidden="true"><i>⌗</i></span>
            <span><strong>Map Creator</strong><small>Build a new projector-ready world, region, dungeon, or encounter.</small></span>
            <b aria-hidden="true">04</b>
          </button>
        </div>
        <button type="button" className={styles.existingCampaign} onClick={openCampaignLibrary}><span>Already have a campaign?</span><strong>Load a previous campaign</strong><b aria-hidden="true">›</b></button>
      </section>
    </div>}

    {campaignLibraryOpen && <div className={styles.modalBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) { setCampaignLibraryOpen(false); setLoadCampaignError(""); loadButtonRef.current?.focus(); } }}>
      <section className={styles.campaignLibrary} role="dialog" aria-modal="true" aria-labelledby="campaign-library-title">
        <button type="button" className={styles.pickerClose} aria-label="Close saved campaigns" onClick={() => { setCampaignLibraryOpen(false); setLoadCampaignError(""); loadButtonRef.current?.focus(); }}>×</button>
        <p className={styles.creditsOverline}>Saved on this device</p>
        <h2 id="campaign-library-title">Load previous campaign</h2>
        <p className={styles.pickerLead}>Choose a saved table. Its campaign type determines which director tools open.</p>
        {storedCampaignsLoading ? <div className={styles.campaignLibraryStatus} role="status"><span className={styles.librarySpinner} aria-hidden="true" />Opening the local campaign library…</div> : null}
        {!storedCampaignsLoading && storedCampaignsError ? <div className={styles.campaignLibraryError} role="alert">{storedCampaignsError}</div> : null}
        {!storedCampaignsLoading && !storedCampaignsError && storedCampaigns.length === 0 ? <div className={styles.campaignLibraryStatus}>No saved campaigns are on this device yet.</div> : null}
        {!storedCampaignsLoading && !storedCampaignsError && storedCampaigns.length > 0 ? <div className={styles.savedCampaignList}>
          {storedCampaigns.map((campaign) => {
            const meta = CAMPAIGN_KIND_META[campaign.kind];
            return <button type="button" key={campaign.id} disabled={Boolean(loadingCampaignId)} onClick={() => loadCampaign(campaign.id)} aria-label={`Load ${campaign.name}, ${meta.label}`}>
              <span className={styles.savedCampaignIcon} aria-hidden="true">{meta.icon}</span>
              <span className={styles.savedCampaignCopy}>
                <span className={styles.savedCampaignHeading}><strong>{campaign.name}</strong>{campaign.active ? <em>Last opened</em> : null}</span>
                <span className={styles.savedCampaignMeta}><b>{meta.label}</b><i aria-hidden="true">◆</i><span>{campaign.sceneCount} {campaign.sceneCount === 1 ? "scene" : "scenes"}</span><i aria-hidden="true">◆</i><time dateTime={new Date(campaign.updatedAt).toISOString()}>Updated {campaignDate(campaign.updatedAt)}</time></span>
              </span>
              <span className={styles.savedCampaignOpen}>{loadingCampaignId === campaign.id ? "Opening…" : "Open ›"}</span>
            </button>;
          })}
        </div> : null}
        {loadCampaignError ? <div className={styles.campaignLibraryError} role="alert">{loadCampaignError}</div> : null}
      </section>
    </div>}

    {creditsOpen && <div className={styles.modalBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) { setCreditsOpen(false); creditsButtonRef.current?.focus(); } }}>
      <section className={styles.creditsDialog} role="dialog" aria-modal="true" aria-labelledby="credits-title">
        <div className={styles.creditsCrest} aria-hidden="true">TT</div>
        <p className={styles.creditsOverline}>A game table made alive</p>
        <h2 id="credits-title">TBLTOP</h2>
        <p className={styles.creditsLead}>Conceived, designed, and built for physical tables, shared screens, real pieces, and the people gathered around them.</p>
        <dl>
          <div><dt>Creative direction</dt><dd>TBLTOP Project</dd></div>
          <div><dt>Made for</dt><dd>Projectors · Table screens · TVs · Player phones</dd></div>
          <div><dt>World generation</dt><dd>Azgaar's Fantasy Map Generator · adapted under the MIT License</dd></div>
          <div><dt>Special thanks</dt><dd>Every DM, dealer, player, and miniature brave enough to step onto the screen.</dd></div>
        </dl>
        <a href="/THIRD_PARTY_NOTICES.txt" target="_blank" rel="noreferrer">Third-party notices</a>
        <button type="button" autoFocus onClick={() => { setCreditsOpen(false); creditsButtonRef.current?.focus(); }}>Return to title</button>
      </section>
    </div>}
  </main>;
}
